import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Banner,
  BlockStack,
  Box,
  Button,
  IndexTable,
  InlineStack,
  Pagination,
  Select,
  Text,
  TextField,
  Tooltip,
} from '@shopify/polaris';
import { ExportIcon } from '@shopify/polaris-icons';
import apiClient from '../utils/api';
import { saveBlob, datedFilename } from '../utils/download';
import RunHealthBanner, { RUN_TYPES } from './RunHealthBanner';
import { headingsFor, emptyRow, itemCountFor, DEFAULT_PAGE_SIZE, PageSizeSelect } from '../utils/table';

/**
 * The row count this table opens on. Kept as the SEED for `pageSize` rather than being read
 * directly, so the size an admin picks is the one that goes to the API — this table pages
 * SERVER-side over thousands of offers, so slicing the response would show ten of the fifty
 * rows the server sent and label it "Page 1 of 5".
 */
const PAGE_SIZE = DEFAULT_PAGE_SIZE;

/**
 * What a retailer lists, as the retailer sent it.
 *
 * The SKU Map below this compares the two catalogues, which means it is Shopify-driven and
 * shows nothing until a brand is connected to the retailer — 4,299 fetched offers read as
 * "0 listed", with no screen on which the difference was visible. This table is the other
 * direction: every offer we hold, mapped or not, owned or not, fulfillable or not.
 *
 * The two derived columns carry the point. "Attributes to" has THREE states rather than a
 * tick, because a brand of the right name existing and an order for it reaching that brand
 * are different facts — the order ingest resolves brands through a connection, so a name
 * match with no connection attributes to nobody. "In Shopify" is brand-blind: an offer whose
 * SKU is in no store at all cannot be fulfilled by anyone.
 */


export const COLUMNS = [
  {
    key: 'retailer',
    title: 'Retailer',
    // Not sortable. Every row on a page carries the same retailer — this table is scoped to
    // one — so there is nothing to order by, and offering a sort that cannot reorder anything
    // is a control that lies. The SKU Map's Retailer column IS sortable, because its rows
    // genuinely span retailers.
    tip: 'Which retailer listed this offer. This table shows one retailer at a time; the '
      + 'selector above switches between them when more than one is connected. The same SKU '
      + 'listed at two retailers is two separate offers with their own stock and price, so '
      + 'it appears once under each.',
  },
  {
    key: 'sku',
    labels: { ascending: 'A-Z', descending: 'Z-A' },
    title: 'Retailer SKU',
    tip: 'The shop_sku the retailer sent. This is the string matched against your Shopify '
      + 'SKUs — it is not necessarily the retailer\'s own internal product code.',
  },
  {
    key: 'brand',
    labels: { ascending: 'A-Z', descending: 'Z-A' },
    title: 'Retailer brand',
    tip: 'product_brand, exactly as the retailer spells it. This is the value that must equal '
      + 'a brand\'s name, its Mirakl brand name, or the connection override — compared '
      + 'lowercased and trimmed. OF21 documents it as optional, and an offer without one is '
      + 'never stored, so it cannot appear here at all.',
  },
  // Two columns used to sit here, with a filter each. "Attributes to" answered, per SKU,
  // which brand an order would reach; "In Shopify" answered whether the code exists in any
  // brand's Shopify catalogue. Both are per-BRAND questions, and the Brands table above
  // answers them once instead of thousands of times down a 4,299-row catalogue. The filters
  // went with them, and so did the server-side machinery behind them — the absent-SKU set,
  // its 25,000-row cap and the segmented sort. This is a plain listing now.
  {
    key: 'stock',
    labels: { ascending: 'Lowest stock first', descending: 'Highest stock first' },
    title: 'Retailer stock',
    tip: 'Quantity the retailer holds for this offer, from the last offer read — not live, '
      + 'and not compared against Shopify here. The SKU Map below is where the two are '
      + 'set side by side with both reading dates.',
  },
  {
    key: 'price',
    labels: { ascending: 'Cheapest first', descending: 'Most expensive first' },
    title: 'Price',
    tip: 'The retailer\'s listed price for the offer, in the account currency.',
  },
  {
    key: 'read',
    labels: { ascending: 'Oldest first', descending: 'Newest first' },
    title: 'Last read',
    tip: 'When this offer was last returned by an offer fetch. A row older than the others '
      + 'means the retailer stopped sending it rather than that it changed.',
  },
];

/**
 * Sortable, in COLUMNS order, and all eight are.
 *
 * "Attributes to" and "In Shopify" are computed rather than stored, so they are sorted by the
 * SERVICE in segments (each of their states is a `where` predicate over the same brand-name
 * lists and absent-SKU set the filters already use) instead of by reordering the fetched page.
 * That distinction is the whole point: this table pages 50 rows over thousands of offers, so a
 * client-side reorder would sort the page and present it as a sort of the catalogue.
 */
// Sortable is now per column rather than "all of them". The Retailer column carries the same
// value on every row of a page — this table shows one retailer at a time — so a sort control
// there could not reorder anything, and a control that cannot do what it offers is worse than
// no control. Every other column still sorts.
export const SORTABLE = COLUMNS.map((c) => Boolean(c.labels));

/**
 * REQUIRED, and required COMPLETE — one entry per sortable heading.
 *
 * Two separate Polaris behaviours make this non-optional. Without the prop, IndexTable returns
 * the bare sort button for a sortable heading and silently DISCARDS its `tooltipContent`, so
 * every `tip` above would vanish the moment the column became sortable. And with a PARTIAL map
 * it reads sortToggleLabels[index][direction] unguarded and throws a TypeError at render. See
 * the same constant in OrderManagement.jsx, which carries it for the first reason.
 *
 * The wording says what the sort does, not which way the arrow points — "A-Z" is readable,
 * "ascending" is not.
 *
 * DERIVED from COLUMNS rather than written as an index map. It was the latter, and an index map
 * beside a list is a standing invitation to the exact defect the order tables shipped: remove
 * or reorder one column and every label below it describes the wrong heading, silently. That
 * happened here the moment the Condition column was dropped — indices 5, 6 and 7 all shifted.
 */
// One entry per SORTABLE heading, and only those. Polaris throws on a partial map, and a
// column with no labels is not sortable — including it would map an index to undefined.
export const SORT_TOGGLE_LABELS = Object.fromEntries(
  COLUMNS.map((c, i) => [i, c.labels]).filter(([, labels]) => Boolean(labels)),
);

const when = (v) => (v ? new Date(v).toLocaleString(undefined, {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
}) : '—');

// The per-SKU brand verdict, its filter, and the whole summary strip were removed together.
// The Brands table above carries all of it per brand — the count, the connection state and
// the retailer's own spelling — and saying it twice on one screen meant two figures nobody
// could reconcile. `summary` is now only `{ brands }`, and only fills the brand dropdown.

export default function RetailerCatalogueTable({ marketplaces = [] }) {
  const [marketplaceId, setMarketplaceId] = useState('');
  const [search, setSearch] = useState('');
  const [productBrand, setProductBrand] = useState('');
  // Seeded with the ordering the API already applies by default, so the heading arrow states
  // what the rows actually are rather than starting blank over a table that is already sorted.
  const [sortBy, setSortBy] = useState('brand');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(1);
  // Seeded with this table's own existing count, so nobody's view changes size unasked.
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Default to the first retailer rather than "all": offers, credentials and the SKU map are
  // all per account, and a merged list across accounts would put two retailers' spellings of
  // the same brand in one column with nothing to tell them apart.
  useEffect(() => {
    if (!marketplaceId && marketplaces.length > 0) setMarketplaceId(marketplaces[0].id);
  }, [marketplaces, marketplaceId]);

  /**
   * Only the NEWEST request may write state — the same guard, and for the same reason, as
   * SkuMapTable and QueueTable.
   *
   * The page reset is its own effect while `load` depends on `page`, so both run in one
   * commit: changing the row count on page 3 fires page 3 at the new limit AND then page 1 at
   * the new limit. Two requests, no cancellation, no ordering guarantee — so the slower one
   * won and page-3 rows sat under "Page 1 of 2".
   */
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    if (!marketplaceId) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const res = await apiClient.getRetailerCatalogue(marketplaceId, {
        search, productBrand, sortBy, sortDirection, page, limit: pageSize,
      });
      if (seq !== requestSeq.current) return;
      setData(res);
      setError(null);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err.message || 'Could not load the retailer catalogue');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [marketplaceId, search, productBrand, sortBy, sortDirection, page, pageSize]);

  useEffect(() => { load(); }, [load]);
  // Any filter change returns to page 1 — otherwise a narrower filter lands on a page that
  // no longer exists and the table reads as empty. A SORT change belongs here for a different
  // reason: the row that was on page 4 under the old order is usually on page 1 under the new
  // one, so staying on page 4 shows the middle of the new ordering.
  // The row count belongs in the same reset: page 4 of a ten-row window is inside page 1 of a
  // fifty-row one, so keeping the number would ask the API for a page that no longer exists and
  // the table would read as "no offer matches these filters".
  useEffect(() => {
    setPage(1);
  }, [marketplaceId, search, productBrand, sortBy, sortDirection, pageSize]);

  // IndexTable speaks column INDEX and 'ascending'/'descending'; the API speaks the column key
  // and 'asc'/'desc'. Translated in one place, off the same COLUMNS array the headings and the
  // toggle labels come from, so the three cannot drift apart.
  const handleSort = useCallback((headingIndex, direction) => {
    const column = COLUMNS[headingIndex];
    // An unsortable heading cannot raise this, but guarding costs nothing and stops a future
    // column without labels quietly setting sortBy to a key the service does not know.
    if (!column || !SORTABLE[headingIndex]) return;
    setSortBy(column.key);
    setSortDirection(direction === 'descending' ? 'desc' : 'asc');
  }, []);

  const summary = data?.summary ?? null;
  const rows = data?.rows ?? [];
  const totalPages = Math.max(Math.ceil((data?.total ?? 0) / pageSize), 1);

  /** The download: this table's filters and sort, with no page. */
  const [exporting, setExporting] = useState(false);
  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const blob = await apiClient.exportRetailerCatalogue(marketplaceId, {
        search, productBrand, sortBy, sortDirection,
      });
      saveBlob(blob, datedFilename('retailer-catalogue'));
    } catch (err) {
      setError(err.message || 'Failed to export the catalogue');
    } finally {
      setExporting(false);
    }
  }, [marketplaceId, search, productBrand, sortBy, sortDirection]);

  const brandOptions = useMemo(() => ([
    { label: 'All retailer brands', value: '' },
    ...(summary?.brands ?? []).map((b) => ({
      label: `${b.productBrand} (${b.skuCount})`,
      value: b.productBrand,
    })),
  ]), [summary]);

  const headings = useMemo(() => headingsFor(COLUMNS), []);
  // Falls back to the first SORTABLE column, not to index 0. Index 0 is Retailer now, which
  // is not sortable — pointing the sort indicator at it would mark a heading as the active
  // sort when it cannot be one.
  const sortColumnIndex = useMemo(() => {
    const found = COLUMNS.findIndex((c) => c.key === sortBy);
    if (found >= 0 && SORTABLE[found]) return found;
    return Math.max(SORTABLE.indexOf(true), 0);
  }, [sortBy]);

  if (marketplaces.length === 0) {
    return (
      <Text tone="subdued" variant="bodySm">
        No retailer accounts yet. Add one and fetch its offers to see what it lists.
      </Text>
    );
  }

  return (
    <BlockStack gap="400">
      {error && <Banner tone="critical">{error}</Banner>}

      {/* Before the controls, not after the table: the warning has to be read BEFORE the
          figures it qualifies, and a caveat under 4,299 rows is a caveat nobody reaches. */}
      <RunHealthBanner marketplaceId={marketplaceId} types={RUN_TYPES.RETAILER_CATALOGUE} />

      <InlineStack gap="300" wrap>
        <Box minWidth="220px">
          <TextField
            label="Search"
            labelHidden
            placeholder="Search retailer SKU or title"
            value={search}
            onChange={setSearch}
            autoComplete="off"
            clearButton
            onClearButtonClick={() => setSearch('')}
          />
        </Box>
        {/* ALWAYS rendered, even with one retailer. It used to be hidden below two, which
            meant the first admin to open this screen was given no sign that the table is
            scoped to a retailer at all — so nothing told them what to expect when a second
            one was connected, and nothing named the one they were looking at. A control that
            explains the shape of the data is not a control to hide. */}
        <Select
          label="Retailer"
          labelHidden
          options={marketplaces.map((m) => ({ label: m.name, value: m.id }))}
          value={marketplaceId}
          onChange={setMarketplaceId}
        />
        <Select
          label="Retailer brand"
          labelHidden
          options={brandOptions}
          value={productBrand}
          onChange={setProductBrand}
        />
        {/* The whole filtered set, not the page on screen — this table runs to thousands of
            offers and the page shows fifty. */}
        <Tooltip content={
          (data?.total ?? 0) === 0
            ? 'Nothing to export with these filters.'
            : `Downloads all ${(data?.total ?? 0).toLocaleString()} matching offers as a CSV, not just this page.`
        }>
          <Button
            size="slim"
            icon={ExportIcon}
            loading={exporting}
            disabled={exporting || (data?.total ?? 0) === 0}
            onClick={exportCsv}
          >
            Export CSV
          </Button>
        </Tooltip>
      </InlineStack>

      <IndexTable
        resourceName={{ singular: 'offer', plural: 'offers' }}
        // 1 when there is nothing, so the header row survives: at itemCount 0 IndexTable swaps
        // the WHOLE table for emptyState, column names included. The emptiness is a row here.
        itemCount={itemCountFor(rows)}
        selectable={false}
        headings={headings}
        loading={loading}
        sortable={SORTABLE}
        // Not optional on a sortable table: without it Polaris drops every heading's
        // tooltipContent, and a partial map throws. See SORT_TOGGLE_LABELS above.
        sortToggleLabels={SORT_TOGGLE_LABELS}
        sortColumnIndex={sortColumnIndex}
        sortDirection={sortDirection === 'desc' ? 'descending' : 'ascending'}
        onSort={handleSort}
      >
        {rows.length === 0
          ? emptyRow(
            COLUMNS,
            'retailer-catalogue',
            search || productBrand
              ? 'No offer matches the search or brand filter.'
              : 'No SKUs held for this retailer yet. Use Fetch SKUs on its row above — '
                + 'it reads the retailer\'s catalogue and needs no brand connection.',
          )
          : rows.map((row, index) => (
            <IndexTable.Row id={row.id} key={row.id} position={index}>
              <IndexTable.Cell>
                {/* From the ROW, not from the selected filter, so the column states what the
                    data says rather than what was asked for. */}
                <Text variant="bodySm">{row.marketplace?.name || '—'}</Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <BlockStack gap="050">
                  <Text variant="bodySm" fontWeight="semibold">{row.shopSku}</Text>
                  {row.productTitle && (
                    <Text variant="bodySm" tone="subdued">{row.productTitle}</Text>
                  )}
                  {/* A red "Not selling at retailer" badge stood here on every offer the
                      retailer had switched off. It read as a fault on a table that is simply
                      a copy of what the retailer sent — this screen lists every offer whether
                      or not a brand is connected to it, so most rows carrying it needed
                      nothing done. Where it IS actionable, the SKU Map says so against the
                      SKUs that are actually mapped to a brand, with the reason beside it.
                      offerActive is still fetched and stored, and still read there. */}
                </BlockStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodySm">{row.productBrand}</Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodySm" numeric>
                  {row.offerQuantity ?? '—'}
                </Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodySm" numeric>
                  {row.offerPrice ?? '—'}
                </Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodySm" tone="subdued">{when(row.lastVerifiedAt)}</Text>
              </IndexTable.Cell>
            </IndexTable.Row>
          ))}
      </IndexTable>

      {/* Both controls in one row, and only once there is something to page through. The
          pager drops out when everything fits, but the row count stays: it is how someone GETS
          a second page, so hiding it at size 50 would strand them there. */}
      {rows.length > 0 && (
        <InlineStack align="space-between" blockAlign="center">
          <PageSizeSelect value={pageSize} onChange={setPageSize} />
          {totalPages > 1 && (
            <Pagination
              hasPrevious={page > 1}
              hasNext={page < totalPages}
              onPrevious={() => setPage((p) => Math.max(p - 1, 1))}
              onNext={() => setPage((p) => p + 1)}
              label={`Page ${page} of ${totalPages} · ${data.total} offer${data.total === 1 ? '' : 's'}`}
            />
          )}
        </InlineStack>
      )}
    </BlockStack>
  );
}
